import Grid from "@mui/material/Grid";

import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@src/components/ai-elements/conversation";

import { CHAT_SCROLL_GUTTER } from "./chatScrollGutter";
import { useExecutionPanelContext } from "./ExecutionPanelContext";
import { TurnStream } from "@src/features/ai/components/WorkflowEditor/execution/turn/TurnStream";
import { IgAiIcon } from "@intelligems/ig-theme/IgAiIcon";
import { MessageSquareMoreIcon } from "lucide-react";

/**
 * The chat-mode transcript: the scrolling conversation of turns, with the
 * optional initial-context input pinned above. Reads all of its inputs from
 * the ExecutionPanel context.
 */
export const ExecutionPanelConversation = () => {
  const { aiChat, getSlot, compact } = useExecutionPanelContext();

  return (
    <Grid
      container
      direction={"column"}
      rowSpacing={1.5}
      sx={{
        flexGrow: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        mt: compact ? 0.5 : 1,
        minHeight: 0,
        // Reaches into the frame's gutter so the scrollbar sits near its
        // edge; the content pads back by the same amount (`chatScrollGutter`).
        mr: `calc(-1 * ${CHAT_SCROLL_GUTTER})`,
        "& [role='log'] > div": { pr: CHAT_SCROLL_GUTTER },
      }}
    >
      {/* 32px of bottom scroll padding (Figma "chat-panel"): the auto-scroll
      stops with the last message 32px clear of the scroller's edge, on top of
      the dock's own 32px gap below it. The padding is on the content, which is
      what the stick-to-bottom scroll measures; `scroll-padding` keeps a
      keyboard or anchor scroll landing in the same place. */}
      <Conversation className={compact ? undefined : "scroll-pb-8"}>
        {/* The single, ALWAYS-mounted StickToBottom.Content: it must wrap all
        scrolling content and never unmount, or the library loses its
        content/scroll refs and auto-scroll stops engaging. The empty state and
        the turns are two mutually-exclusive views *inside* it — never a reason
        to conditionally mount Content itself. */}
        <ConversationContent className={compact ? "p-0.5" : "pb-8"}>
          {aiChat.turns.length === 0 ? (
            // Overlay the full content area (Content is `relative`) so the empty
            // state can center itself without needing turns-in-flow height.
            <div className="absolute inset-0 flex flex-col">
              {getSlot(
                "conversationEmpty",
                <ConversationEmptyState
                  icon={
                    <IgAiIcon
                      icon={MessageSquareMoreIcon}
                      surface="round"
                      shine
                    />
                  }
                  title="Start a conversation"
                  description="Messages will appear here as the conversation progresses."
                />
              )}
            </div>
          ) : (
            <>
              {getSlot("conversationHeader")}
              <TurnStream />
            </>
          )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>
    </Grid>
  );
};
